import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {fadeInRightAnimation} from '../../../@fury/animations/fade-in-right.animation';
import {scaleInAnimation} from '../../../@fury/animations/scale-in.animation';
import {fadeInUpAnimation} from '../../../@fury/animations/fade-in-up.animation';
import {Subject} from 'rxjs';
import {FiltersService} from '../../services/base/filters.service';
import {debounceTime, distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import {Company} from '../../interfaces/info/company';
import * as moment from 'moment';
import {IntegratedOptions} from '../../interfaces/base/integrated-options';
import {NationaalGeoregisterSuggest} from '../../interfaces/external-services/nationaal-georegister-suggest';
import {PdokService} from '../../services/external/pdok.service';
import {LeafletMapService} from '../../services/base/leaflet-map.service';
import {NationaalGeoregisterRevGeoServerDocs} from '../../interfaces/external-services/nationaal-georegister-rev-geo';
import {NationaalGeoregisterLookupServerDocs} from '../../interfaces/external-services/nationaal-georegister-lookup';
import {WebformService} from '../../services/reports/webform.service';
import {Router} from '@angular/router';
import {LayoutService} from '../../layout/layout.service';
import {FormService} from '../../services/base/form.service';
import {Title} from '@angular/platform-browser';
import * as _ from 'lodash';

@Component({
  selector: 'fury-company-form',
  templateUrl: './company-form.component.html',
  styleUrls: ['./company-form.component.scss'],
  animations: [fadeInUpAnimation, fadeInRightAnimation, scaleInAnimation]
})
export class CompanyFormComponent implements OnInit, OnDestroy {

  protected readonly Validators = Validators;

  public filters = {
    'personPrefix': {
      preload: true,
      isLoading: false,
      data: <IntegratedOptions[]>[]
    },
    'companies': {
      preload: false,
      isLoading: false,
      data: <Company[]>[]
    },
  };
  public currentSection: string = 'melding';
  public controlForm: boolean = false;
  public suggestSuspectLocations: any = null;
  public isLoadingSuspectLocations: boolean = false;
  public suggestReportLocations: any = null;
  public isLoadingReportLocations: boolean = false;
  public isLoading: boolean = null;
  public noticedAtCheck: boolean = false;

  private destroy$: Subject<null> = new Subject<null>();

  constructor(private filtersService: FiltersService,
              private pdokService: PdokService,
              private cd: ChangeDetectorRef,
              private router: Router,
              private layoutService: LayoutService,
              private titleService: Title,
              public formService: FormService,
              public leafletMapService: LeafletMapService,
              public webformService: WebformService,
  ) {
  }

  ngOnInit(): void {
    this.titleService.setTitle('Bedrijfsmelding - Mileu Klachten Centrale');
    this.filtersService.filters = this.filters;
    this.webformService.formType = 'company';
    this.formService.form = this.webformService.normalForm;
    this.layoutService.isPublicNotificationVisible.emit(false);

    this.webformService.companyForm.controls.form_filling_start_at.setValue(moment().toISOString());

    Object.keys(this.filters).map((key: string): void => {
      if (this.filters[key].preload) {
        this.filtersService[key]().pipe(takeUntil(this.destroy$)).subscribe((data: any): void => {
          if (data) {
            if (key === 'reporterTypes') {
              this.filters[key].data = data.reverse();
            } else {
              this.filters[key].data = data;
            }
          }
        });
      }
    });

    this.webformService.companyForm.controls.noticed_at.setValue(moment().format('YYYY-MM-DD HH:mm'));

    this.webformService.companyForm.controls.end_at.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((): void => {
      const controlNoticedAt = this.webformService.companyForm.controls.noticed_at,
        controlEndAt = this.webformService.companyForm.controls.end_at;

      if (moment(controlEndAt.value) < moment(controlNoticedAt.value)) {
        controlEndAt.setErrors({invalid: true});
      }
    });

    this.webformService.companyForm.controls.noticed_at.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value): void => {
      this.noticedAtCheck = false;
      // check if date is more than 6 months behind
      if (moment(value).isBefore(moment().subtract(6, 'months'))) {
        this.noticedAtCheck = true;
      }
    });

    this.webformService.companyForm.controls.suspect_name.valueChanges.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      filter((query: string): boolean | null => query && (typeof query === 'object' || typeof query === 'string' && (query.length > 2 ? query.length > 2 : null))),
      takeUntil(this.destroy$)
    ).subscribe((value: string | Company): void => {
      this.filters.companies.data = [];
      this.webformService.companyForm.controls.suspect_company_id.setValue(null);
      this.webformService.companyForm.controls.suspect_location.enable();
      this.webformService.companyForm.controls.suspect_location_valid.setValue(false);

      if (value) {
        if (typeof value === 'string') {
          this.filters.companies.isLoading = true;

          this.filtersService.companies(value).pipe(takeUntil(this.destroy$)).subscribe((data: Company[]): void => {
              this.filters.companies.isLoading = false;

              if (data) {
                this.filters.companies.data = <Company[]>data;
              }
            },
            (): boolean => this.filters.companies.isLoading = false);
        } else {
          this.webformService.companyForm.controls.suspect_name.setValue(value.name, {emitEvent: false});
          this.webformService.companyForm.controls.suspect_company_id.setValue(value.id);
          this.webformService.companyForm.controls.suspect_location_valid.setValue(true);
          this.webformService.companyForm.controls.suspect_location.disable();
          this.webformService.companyForm.controls.suspect_location.setValue(value.location, {emitEvent: false});
          this.webformService.companyForm.controls.suspect_postalcode.setValue(null);
          this.webformService.companyForm.controls.suspect_house_number.setValue(null);
          this.webformService.companyForm.controls.suspect_house_number_addition.setValue(null);
          this.webformService.companyForm.controls.suspect_streetname.setValue(null);
          this.webformService.companyForm.controls.suspect_city.setValue(null);
          this.webformService.companyForm.controls.suspect_municipality.setValue(null);
          this.webformService.companyForm.controls.suspect_lat.setValue(null);
          this.webformService.companyForm.controls.suspect_lng.setValue(null);
        }
      }
    });

    this.webformService.companyForm.controls.suspect_location.valueChanges.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      filter((query: string): boolean | null => query && (typeof query === 'object' || typeof query === 'string' && (query.length > 2 ? query.length > 2 : null))),
      takeUntil(this.destroy$)
    ).subscribe((value: string | any): void => {
      this.suggestSuspectLocations = [];

      this.webformService.companyForm.controls.suspect_location_valid.setValue(false);
      this.webformService.companyForm.controls.suspect_postalcode.setValue(null);
      this.webformService.companyForm.controls.suspect_house_number.setValue(null);
      this.webformService.companyForm.controls.suspect_house_number_addition.setValue(null);
      this.webformService.companyForm.controls.suspect_streetname.setValue(null);
      this.webformService.companyForm.controls.suspect_city.setValue(null);
      this.webformService.companyForm.controls.suspect_municipality.setValue(null);
      this.webformService.companyForm.controls.suspect_lat.setValue(null);
      this.webformService.companyForm.controls.suspect_lng.setValue(null);

      if (value) {
        if (typeof value === 'string') {
          this.isLoadingSuspectLocations = true;

          this.pdokService.getSearchedAddress(value).pipe(takeUntil(this.destroy$)).subscribe((response: NationaalGeoregisterSuggest): void => {
              this.isLoadingSuspectLocations = false;

              if (response) {
                if (response.response.numFound > 1) {
                  this.suggestSuspectLocations = response.response.docs;
                } else {
                  this.suggestSuspectLocations = [{
                    id: null,
                    weergavenaam: 'Er zijn geen resultaten gevonden, probeer een ander adres.'
                  }];
                }
              }
            },
            (): boolean => this.isLoadingSuspectLocations = false);
        } else {
          this.pdokService.selectSearchedAddress(value.id).pipe(takeUntil(this.destroy$)).subscribe((response: NationaalGeoregisterLookupServerDocs): void => {
            this.webformService.companyForm.controls.suspect_location_valid.setValue(true);
            this.webformService.companyForm.controls.suspect_location.setValue(response.weergavenaam, {emitEvent: false});
            this.webformService.companyForm.controls.suspect_postalcode.setValue(typeof response.postcode !== 'undefined' ? response.postcode : null);
            this.webformService.companyForm.controls.suspect_house_number.setValue(typeof response.huisnummer !== 'undefined' ? response.huisnummer : null);
            this.webformService.companyForm.controls.suspect_house_number_addition.setValue(typeof response.huisletter !== 'undefined' ? response.huisletter : null);
            this.webformService.companyForm.controls.suspect_streetname.setValue(typeof response.straatnaam !== 'undefined' ? response.straatnaam : null);
            this.webformService.companyForm.controls.suspect_city.setValue(typeof response.woonplaatsnaam !== 'undefined' ? response.woonplaatsnaam : null);
            this.webformService.companyForm.controls.suspect_municipality.setValue(typeof response.gemeentenaam !== 'undefined' ? response.gemeentenaam : null);
            this.webformService.companyForm.controls.suspect_lat.setValue(typeof response.lat !== 'undefined' ? response.lat : null);
            this.webformService.companyForm.controls.suspect_lng.setValue(typeof response.lng !== 'undefined' ? response.lng : null);
          });
        }
      }
    });

    this.webformService.companyForm.controls.report_location.valueChanges.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      filter((query: string): boolean | null => query && (typeof query === 'object' || typeof query === 'string' && (query.length > 2 ? query.length > 2 : null))),
      takeUntil(this.destroy$)
    ).subscribe((value: string | any): void => {
      this.suggestReportLocations = [];

      this.webformService.companyForm.controls.report_location_valid.setValue(false);
      this.webformService.companyForm.controls.report_postalcode.setValue(null);
      this.webformService.companyForm.controls.report_house_number.setValue(null);
      this.webformService.companyForm.controls.report_house_number_addition.setValue(null);
      this.webformService.companyForm.controls.report_streetname.setValue(null);
      this.webformService.companyForm.controls.report_city.setValue(null);
      this.webformService.companyForm.controls.report_municipality.setValue(null);
      this.webformService.companyForm.controls.report_lat.setValue(null);
      this.webformService.companyForm.controls.report_lng.setValue(null);

      if (value) {
        if (typeof value === 'string') {
          this.isLoadingReportLocations = true;

          this.pdokService.getSearchedAddress(value).pipe(takeUntil(this.destroy$)).subscribe((response: NationaalGeoregisterSuggest): void => {
              this.isLoadingReportLocations = false;

              if (response) {
                if (response.response.numFound > 1) {
                  this.suggestReportLocations = response.response.docs;
                } else {
                  this.suggestReportLocations = [{
                    id: null,
                    weergavenaam: 'Er zijn geen resultaten gevonden, probeer een ander adres.'
                  }];
                }
              }
            },
            (): boolean => this.isLoadingReportLocations = false);
        } else {
          this.pdokService.selectSearchedAddress(value.id).pipe(takeUntil(this.destroy$)).subscribe((response: NationaalGeoregisterLookupServerDocs): void => {
            this.webformService.companyForm.controls.report_location_valid.setValue(true);
            this.webformService.companyForm.controls.report_location.setValue(response.weergavenaam, {emitEvent: false});
            this.webformService.companyForm.controls.report_postalcode.setValue(typeof response.postcode !== 'undefined' ? response.postcode : null);
            this.webformService.companyForm.controls.report_house_number.setValue(typeof response.huisnummer !== 'undefined' ? response.huisnummer : null);
            this.webformService.companyForm.controls.report_house_number_addition.setValue(typeof response.huisletter !== 'undefined' ? response.huisletter : null);
            this.webformService.companyForm.controls.report_streetname.setValue(typeof response.straatnaam !== 'undefined' ? response.straatnaam : null);
            this.webformService.companyForm.controls.report_city.setValue(typeof response.woonplaatsnaam !== 'undefined' ? response.woonplaatsnaam : null);
            this.webformService.companyForm.controls.report_municipality.setValue(typeof response.gemeentenaam !== 'undefined' ? response.gemeentenaam : null);
            this.webformService.companyForm.controls.report_lat.setValue(typeof response.lat !== 'undefined' ? response.lat : null);
            this.webformService.companyForm.controls.report_lng.setValue(typeof response.lng !== 'undefined' ? response.lng : null);

            this.leafletMapService.updateMarker([response.lat, response.lng]);
          });
        }
      }
    });

    this.webformService.companyForm.controls.reporter_initials.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string): void => {
      if (value) {
        const control: FormControl<string> = this.webformService.companyForm.controls.reporter_initials;
        if (this.webformService.companyForm.controls['reporter_initials'] !== null && this.webformService.companyForm.value['reporter_initials'].length > value.length) {
          const newValue: string = [...value.slice(0, -2).toUpperCase().replace(/\./g, '')].join('.');
          control.setValue((newValue.length ? newValue + '.' : ''), {emitEvent: false});
        } else {
          control.setValue([...value.toUpperCase().replace(/\./g, '')].join('.') + '.', {emitEvent: false});
        }
      }
    });

    this.webformService.companyForm.controls.reporter_lastname.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string): void => {
      if (value.length > 0) {
//        this.webformService.companyForm.controls.reporter_lastname.setValue(value[0].toUpperCase() + value.slice(1).toLowerCase(), {emitEvent: false});
        this.webformService.companyForm.controls.reporter_lastname.setValue(
          value.replace(/(^\w)(\S*)/g, (m0: string, m1: string, m2: string) => m1.toUpperCase() + m2),
          {emitEvent: false}
        );
      }
    });

    this.webformService.companyForm.controls.reporter_notify.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean): void => {
      const element: FormControl = this.webformService.companyForm.controls.reporter_email;
      if (value) {
        element.addValidators(Validators.required);
      } else {
        element.removeValidators(Validators.required);
      }
      element.updateValueAndValidity();
    });

    if (this.formService.errors !== null) {
      _.forEach(this.formService.errors, (error, key): void => {
        this.webformService.companyForm.get(key).setErrors({invalid: Array.isArray(error) ? error.join('') : error});
      });
    }
  }

  onSectionChange(section: string): void {
    this.currentSection = section ?? 'melding';
    this.cd.detectChanges();
  }

  formatSuspectNameDropdown(result: any): string {
    return result !== null && typeof result === 'object' ? result.name : result;
  }

  formatLocationDropdown(result: any): string {
    return result !== null && typeof result === 'object' ? result.weergavenaam : result;
  }

  onMapClick(event: any): void {
    this.webformService.companyForm.controls.report_location.setValue(null);

    this.pdokService.doReverseSearch([event.latlng.lat, event.latlng.lng]).pipe(takeUntil(this.destroy$)).subscribe((result: NationaalGeoregisterRevGeoServerDocs): void => {
      if (result) {
        this.pdokService.selectSearchedAddress(result.id).pipe(takeUntil(this.destroy$)).subscribe((response: NationaalGeoregisterLookupServerDocs): void => {
          this.webformService.companyForm.controls.report_location_valid.setValue(true);
          this.webformService.companyForm.controls.report_location.setValue(response.weergavenaam, {emitEvent: false});
          this.webformService.companyForm.controls.report_postalcode.setValue(typeof response.postcode !== 'undefined' ? response.postcode : null);
          this.webformService.companyForm.controls.report_house_number.setValue(typeof response.huisnummer !== 'undefined' ? response.huisnummer : null);
          this.webformService.companyForm.controls.report_house_number_addition.setValue(typeof response.huisletter !== 'undefined' ? response.huisletter : null);
          this.webformService.companyForm.controls.report_streetname.setValue(typeof response.straatnaam !== 'undefined' ? response.straatnaam : null);
          this.webformService.companyForm.controls.report_city.setValue(typeof response.woonplaatsnaam !== 'undefined' ? response.woonplaatsnaam : null);
          this.webformService.companyForm.controls.report_municipality.setValue(typeof response.gemeentenaam !== 'undefined' ? response.gemeentenaam : null);
          this.webformService.companyForm.controls.report_lat.setValue(typeof response.lat !== 'undefined' ? response.lat : null);
          this.webformService.companyForm.controls.report_lng.setValue(typeof response.lng !== 'undefined' ? response.lng : null);

          this.leafletMapService.updateMarker([response.lat, response.lng]);
        });
      }
    });
  }

  checkForm(): void {
    this.controlForm = true;
    if (this.webformService.companyForm.valid) {
      this.webformService.checkForm = true;
      this.router.navigate(['/controle']).then((): void => {
      });
    }
  }

  validateReportLocation(): void {
    const controls = this.webformService.companyForm.controls;
    if (controls.report_location.value) {
      if (controls.report_lat.value === null) {
        controls.report_location.setErrors({invalid: true});
      } else {
        controls.report_location.setErrors(null);
      }
    }
  }

  get isLocal(): boolean {
    return window.location.href.indexOf('localhost') !== -1;
  }

  log(): void {
    console.log(this.webformService.companyForm);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
